import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'utils/trpc-client-helper';
import { useSession } from '@/lib/auth-client';
import { Button } from './base/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './base/ui/select';
import { Badge } from './base/ui/badge';
import {
  ArrowLeft,
  Crown,
  User,
  ChevronDown,
  Settings,
  ChevronUp,
} from 'lucide-react';

function AdminHeaderContent({ session }: { session: any }) {
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleCollapsed = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
  };

  // Get current organization
  const { data: currentOrg } = useQuery(trpc.org.current.queryOptions());

  // Get all organizations for switching
  const { data: allOrgs } = useQuery(
    trpc.org.admin.list.queryOptions({ limit: 100 })
  );

  // Impersonate mutation
  const impersonateMutation = useMutation(
    trpc.org.admin.impersonate.mutationOptions({
      onSuccess: () => {
        // Invalidate current org query to refresh
        queryClient.invalidateQueries({
          queryKey: trpc.org.current.queryKey(),
        });
        // Navigate to home to see the impersonated org
        navigate('/home');
      },
    })
  );

  const handleOrgSwitch = (orgId: string) => {
    console.log('switching to org', orgId);
    impersonateMutation.mutate({ organizationId: orgId });
  };

  const getUserDisplayName = () => {
    const user = session?.user;
    return user?.name || user?.email || 'Admin User';
  };

  // Collapsed state - show as FAB in bottom right
  if (isCollapsed) {
    return (
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <Button
          onClick={toggleCollapsed}
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-xl transition-all duration-200"
          title="Show admin panel"
        >
          <Settings className="h-5 w-5 sm:h-6 sm:w-6" />
        </Button>
      </div>
    );
  }

  // Expanded state - floating panel above FAB
  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
      {/* Floating Admin Panel */}
      <div className="mb-3 sm:mb-4 bg-white border border-amber-200 rounded-lg shadow-xl p-3 sm:p-4 w-72 sm:w-80 max-w-[calc(100vw-2rem)] absolute bottom-full right-0">
        <div className="flex items-center justify-between mb-3">
          <Badge
            variant="secondary"
            className="bg-amber-100 text-amber-800 text-xs"
          >
            <Crown className="h-3 w-3 mr-1" />
            Admin Panel
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleCollapsed}
            className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
            title="Hide admin panel"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-3">
          {/* User Info */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <User className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{getUserDisplayName()}</span>
          </div>

          {/* Organization Switcher */}
          {allOrgs && allOrgs.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                Switch Organization
              </label>
              <Select
                value={currentOrg?.id || ''}
                onValueChange={handleOrgSwitch}
                disabled={impersonateMutation.isPending}
              >
                <SelectTrigger className="w-full h-8 text-xs bg-white border-gray-300">
                  <SelectValue placeholder="Switch organization..." />
                </SelectTrigger>
                <SelectContent>
                  {allOrgs.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      <div className="flex items-center gap-2">
                        <span className="truncate">{org.name}</span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <User className="h-3 w-3" />
                          <span className="text-xs">
                            {org._count?.members || 0}
                          </span>
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Back to Admin Button */}
          <Button
            variant="outline"
            size="sm"
            asChild
            className="w-full h-8 text-xs border-amber-300 hover:bg-amber-50"
          >
            <Link to="/admin" className="flex items-center justify-center">
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back to Admin
            </Link>
          </Button>
        </div>
      </div>

      {/* FAB Button */}
      <Button
        onClick={toggleCollapsed}
        className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-xl transition-all duration-200"
        title="Hide admin panel"
      >
        <ChevronUp className="h-5 w-5 sm:h-6 sm:w-6" />
      </Button>
    </div>
  );
}

function AdminHeaderClient() {
  const { data: session, isPending } = useSession();

  // Don't render anything while loading session
  if (isPending) {
    return null;
  }

  // Only render for super admins - check IMMEDIATELY
  if (!(session?.user as any)?.admin) {
    return null;
  }

  return <AdminHeaderContent session={session} />;
}

export function AdminHeader() {
  const [isClient, setIsClient] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;

  // Ensure this only runs on the client
  useEffect(() => {
    setIsClient(true);
  }, []);

  let isStorybook = false;
  if (typeof window !== 'undefined') {
    isStorybook = window.location.port === '6006';
  }

  // Don't render anything on the server
  if (!isClient || pathname.includes('/admin') || isStorybook) {
    return null;
  }

  return <AdminHeaderClient />;
}
